import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, FileSearch } from "lucide-react";
import { Seo } from "@/components/Seo";
import { AdUnit } from "@/components/AdUnit";
import { ToolIcon } from "@/components/iconMap";
import { SeoSections } from "@/components/SeoSections";

const MARKETS = [
  { name: "Amazon", icon: "Package", desc: "Label and invoice share one page; the crop keeps the SKU/Qty line.", path: "/tools/amazon-shipping-label-crop", cta: "Amazon Shipping Label Cropper" },
  { name: "Flipkart", icon: "Truck", desc: "Full-page label+invoice combo; the crop keeps a full-width label.", path: "/tools/flipkart-shipping-label-crop", cta: "Flipkart Shipping Label Cropper" },
  { name: "Meesho", icon: "ShoppingBag", desc: "Label runs from Customer Address to Product Details, with a tax-invoice strip alongside.", path: "/tools/meesho-shipping-label-crop", cta: "Meesho Shipping Label Cropper" },
  { name: "Shopsy", icon: "Store", desc: "Uses the same layout as Flipkart.", path: "/tools/shopsy-shipping-label-crop", cta: "Shopsy Shipping Label Crop" },
];

const FAQ = [
  { q: "Why isn't there one tool that crops any marketplace's PDF?", a: "Because each marketplace places the label, barcode, and invoice in different positions on the page. A layout-specific crop is more reliable than a generic one, so we built one tool per marketplace." },
  { q: "What if I only need the invoice, not the label?", a: "Use the Invoice Cropper tool, which extracts the invoice portion instead of the label." },
  { q: "Are these tools free?", a: "Yes, all of them are free, browser-based, and don't require an account." },
  { q: "Is my PDF uploaded to a server?", a: "No, cropping happens entirely in your browser for every marketplace-specific tool on this site." },
];

const LINKS = [
  { label: "Merge Marketplace Labels", path: "/tools/merge-marketplace-labels" },
  { label: "Invoice Cropper", path: "/tools/invoice-cropper" },
  { label: "A4 Label Maker", path: "/tools/a4-label-maker" },
  { label: "Guide: How to Crop Amazon Shipping Labels", path: "/blog/how-to-crop-amazon-shipping-labels-from-pdf" },
];

const JSONLD = [{
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "PDF Shipping Label Cropper",
  url: "https://ecroplyai.in/tools/pdf-shipping-label-cropper",
}];

export default function PdfCropperHub() {
  return (
    <>
      <Seo
        title="PDF Shipping Label Cropper - Amazon, Flipkart, Meesho"
        description="Free tool to crop shipping labels out of any marketplace PDF. Pick your marketplace below – Amazon, Flipkart, Meesho or Shopsy – and crop in seconds."
        path="/tools/pdf-shipping-label-cropper"
        faq={FAQ}
        jsonLd={JSONLD}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Ecommerce Tools", path: "/ecommerce-tools" }, { name: "PDF Shipping Label Cropper", path: "/tools/pdf-shipping-label-cropper" }]}
      />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/ecommerce-tools" className="hover:text-white">Ecommerce Tools</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-200">PDF Shipping Label Cropper</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
              <FileSearch className="w-7 h-7 text-blue-300" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">PDF Shipping Label Cropper</h1>
              <p className="mt-3 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
                Every marketplace formats its shipping label PDF differently. Pick yours and crop out the invoice in seconds — free and browser-based.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="space-y-4 text-base text-slate-600 leading-relaxed">
          <p>"Cropping a shipping label PDF" means something different depending on which marketplace generated the file — Amazon, Flipkart, Meesho and Shopsy each place the label and invoice differently on the page, so a single generic crop doesn't work well across all of them. This page explains what a shipping label crop actually does and routes you to the tool tuned to your marketplace's exact layout.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-3">What "cropping" removes</h2>
        <p className="text-base text-slate-600 leading-relaxed">
          Most marketplaces bundle the shipping label and the tax invoice into one PDF page. Printing that whole page on label stock wastes material and can place invoice text uncomfortably close to the barcode. Cropping trims the page down to just the label region — barcode, address, and SKU/Qty line — and discards or separates the invoice.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-5">Pick your marketplace</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {MARKETS.map((m) => (
            <Link key={m.name} to={m.path} data-testid={`hub-market-${m.name.toLowerCase()}`}
              className="group bg-white border border-slate-200 hover:border-blue-400 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/5 hover:-translate-y-1 flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors flex items-center justify-center mb-4">
                <ToolIcon name={m.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-900">{m.name}</h3>
              <p className="mt-2 text-sm text-slate-600 flex-1">{m.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">
                {m.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-5 bg-indigo-50 border border-indigo-200 rounded-2xl p-5">
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong className="text-slate-900">Selling on more than one marketplace?</strong> Crop each file with its matching tool, then combine everything into one print run with{" "}
            <Link to="/tools/merge-marketplace-labels" className="text-indigo-700 font-semibold hover:underline">Merge Marketplace Labels</Link>.
          </p>
        </div>
        <AdUnit />
      </section>

      <SeoSections
        howTo={[
          "Download the label PDF from your marketplace's seller panel.",
          "Open the tool matching that marketplace from the list above.",
          "Upload or drag in the PDF.",
          "Preview the cropped result.",
          "Download and print, or merge with labels from other marketplaces first.",
        ]}
        faq={FAQ}
        links={LINKS}
      />
    </>
  );
}
